/**
 * RT take sidecar — `<fileBase>.json` written next to the BWF file.
 *
 * Captures everything QC and conform need that doesn't fit in bext:
 * MTC/timecode broadcast log, capture device, the AudioContext's actual sample
 * rate (vs the nominal 48 k), dropout events and network quality samples.
 */

export interface RtSidecarMtcEntry {
  /** Samples since midnight @48 kHz, as broadcast on the 'rt' data channel. */
  samples: number
  /** Wall-clock ms (performance.timeOrigin + now) when the packet arrived. */
  wallMs: number
}

export interface RtSidecarDropout {
  /** Sample position in the take where frames went missing. */
  atSample: number
  /** Number of missing frames. */
  missedFrames: number
}

export interface RtSidecarNetworkSample {
  wallMs: number
  rttMs: number
  lossPct: number
}

export interface RtSidecar {
  version: 1
  fileName: string
  fileBase: string
  tcStartSamples: number
  sampleRateNominal: number
  /** AudioContext.sampleRate at capture time (clock-drift forensics). */
  sampleRateActual: number
  bitDepth: number
  channels: number
  deviceLabel: string | null
  durationSec: number
  /** Total PCM data bytes (excluding header). */
  dataBytes: number
  mtcLog: RtSidecarMtcEntry[]
  dropouts: RtSidecarDropout[]
  networkSamples: RtSidecarNetworkSample[]
  createdAt: string
}

export interface BuildSidecarOptions {
  fileBase: string
  tcStartSamples: number
  sampleRateActual: number
  durationSec: number
  dataBytes: number
  deviceLabel?: string | null
  mtcLog?: RtSidecarMtcEntry[]
  dropouts?: RtSidecarDropout[]
  networkSamples?: RtSidecarNetworkSample[]
  /** Defaults to now; injectable for tests. */
  createdAt?: Date
}

export function buildSidecar(opts: BuildSidecarOptions): RtSidecar {
  return {
    version: 1,
    fileName: `${opts.fileBase}.wav`,
    fileBase: opts.fileBase,
    tcStartSamples: opts.tcStartSamples,
    sampleRateNominal: 48000,
    sampleRateActual: opts.sampleRateActual,
    bitDepth: 24,
    channels: 1,
    deviceLabel: opts.deviceLabel ?? null,
    durationSec: opts.durationSec,
    dataBytes: opts.dataBytes,
    mtcLog: opts.mtcLog ?? [],
    dropouts: opts.dropouts ?? [],
    networkSamples: opts.networkSamples ?? [],
    createdAt: (opts.createdAt ?? new Date()).toISOString(),
  }
}
